import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader, getRequestIP } from "@tanstack/react-start/server";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { requireAdminOrModerator } from "./auth-roles.server";
import type { Json } from "@/integrations/supabase/types";
import { checkRateLimit } from "./rate-limit.server";

const consentSchema = z.object({
  necessary: z.boolean(),
  analytics: z.boolean(),
  marketing: z.boolean(),
});

const heartbeatSchema = z.object({
  visitorId: z.string().uuid(),
  sessionId: z.string().uuid(),
  consent: consentSchema,
  currentPath: z.string().min(1).max(500),
  landingPath: z.string().min(1).max(500).optional(),
  referrer: z.string().max(2000).optional(),
  isFirstHit: z.boolean().optional(),
});

const consentInputSchema = z.object({
  visitorId: z.string().uuid(),
  consent: consentSchema,
});

const marketingContactSchema = z.object({
  visitorId: z.string().uuid(),
  source: z.enum(["chat", "contact_form", "inquiry", "test_drive", "newsletter"]),
  email: z.string().email().optional(),
  phone: z.string().max(40).optional(),
  fullName: z.string().max(200).optional(),
  city: z.string().max(120).optional(),
  metadata: z.unknown().optional(),
});

function getGeoFromCloudflare() {
  const city = getRequestHeader("cf-ipcity") ?? null;
  const region = getRequestHeader("cf-region") ?? null;
  const country = getRequestHeader("cf-ipcountry") ?? null;
  const lat = getRequestHeader("cf-iplatitude");
  const lng = getRequestHeader("cf-iplongitude");
  return {
    city: city ? decodeURIComponent(city) : null,
    region: region ? decodeURIComponent(region) : null,
    country,
    latitude: lat ? Number(lat) : null,
    longitude: lng ? Number(lng) : null,
  };
}

function getClientIp(): string | null {
  const cf = getRequestHeader("cf-connecting-ip");
  if (cf) return cf.trim();
  const xff = getRequestHeader("x-forwarded-for");
  if (xff) return xff.split(",")[0].trim();
  try {
    return getRequestIP({ xForwardedFor: true }) ?? null;
  } catch {
    return null;
  }
}

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input.trim().toLowerCase());
  const buf = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Persist or refresh consent for a visitor. */
export const recordConsent = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => consentInputSchema.parse(input))
  .handler(async ({ data }) => {
    try {
      checkRateLimit("consent", 30, 60_000);
    } catch {
      return { success: false, error: "rate_limited" };
    }

    const ip = getClientIp();
    const ua = getRequestHeader("user-agent") ?? null;

    const { error } = await supabaseAdmin
      .from("visitor_consents")
      .upsert(
        {
          visitor_id: data.visitorId,
          necessary: data.consent.necessary,
          analytics: data.consent.analytics,
          marketing: data.consent.marketing,
          ip_address: ip,
          user_agent: ua,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "visitor_id" },
      );

    if (error) {
      console.error("recordConsent failed", error);
      return { success: false, error: "db_error" };
    }
    return { success: true };
  });

/** Heartbeat: create or update an active visitor session with geo data. */
export const trackHeartbeat = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => heartbeatSchema.parse(input))
  .handler(async ({ data }) => {
    try {
      checkRateLimit("visitor-heartbeat", 60, 60_000);
    } catch {
      return { success: false };
    }

    // Skip entirely if analytics consent not granted
    if (!data.consent.analytics) {
      return { success: true, skipped: true as const };
    }

    const geo = getGeoFromCloudflare();
    const ip = getClientIp();
    const ua = getRequestHeader("user-agent") ?? null;
    const now = new Date().toISOString();

    // Upsert session row
    const { error } = await supabaseAdmin.from("visitor_sessions").upsert(
      {
        visitor_id: data.visitorId,
        session_id: data.sessionId,
        city: geo.city,
        region: geo.region,
        country: geo.country,
        latitude: geo.latitude,
        longitude: geo.longitude,
        ip_address: ip,
        user_agent: ua,
        referrer: data.referrer ?? null,
        landing_path: data.landingPath ?? data.currentPath,
        current_path: data.currentPath,
        consent_analytics: data.consent.analytics,
        consent_marketing: data.consent.marketing,
        last_seen_at: now,
      },
      { onConflict: "session_id" },
    );

    if (error) {
      console.error("trackHeartbeat upsert failed", error);
      return { success: false };
    }

    // Increment page_views on path change (best-effort, non-blocking)
    if (data.isFirstHit) {
      await supabaseAdmin.from("visitor_events").insert({
        visitor_id: data.visitorId,
        session_id: data.sessionId,
        event_type: "page_view",
        event_path: data.currentPath,
      });
    }

    return { success: true };
  });

/** Record a marketing contact (chat lead, form submit). Stores hashed PII for CAPI. */
export const recordMarketingContact = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => marketingContactSchema.parse(input))
  .handler(async ({ data }) => {
    try {
      checkRateLimit("marketing-contact", 10, 60_000);
    } catch {
      return { success: false, error: "rate_limited" };
    }

    // Check consent
    const { data: consent } = await supabaseAdmin
      .from("visitor_consents")
      .select("marketing")
      .eq("visitor_id", data.visitorId)
      .maybeSingle();

    const hasMarketingConsent = consent?.marketing === true;

    const emailHash = data.email ? await sha256Hex(data.email) : null;
    const phoneHash = data.phone ? await sha256Hex(data.phone.replace(/[^\d+]/g, "")) : null;
    const cityHash = data.city ? await sha256Hex(data.city) : null;

    let firstNameHash: string | null = null;
    let lastNameHash: string | null = null;
    if (data.fullName) {
      const parts = data.fullName.trim().split(/\s+/);
      firstNameHash = await sha256Hex(parts[0]);
      if (parts.length > 1) {
        lastNameHash = await sha256Hex(parts[parts.length - 1]);
      }
    }

    const { error } = await supabaseAdmin.from("marketing_contacts").insert({
      visitor_id: data.visitorId,
      source: data.source,
      email_hash: emailHash,
      phone_hash: phoneHash,
      first_name_hash: firstNameHash,
      last_name_hash: lastNameHash,
      city_hash: cityHash,
      // Store plain PII only if user gave marketing consent (still used for CAPI fallback / admin display)
      email_plain: hasMarketingConsent ? data.email ?? null : null,
      phone_plain: hasMarketingConsent ? data.phone ?? null : null,
      full_name_plain: hasMarketingConsent ? data.fullName ?? null : null,
      city: data.city ?? null,
      consent_marketing: hasMarketingConsent,
      metadata: (data.metadata ?? null) as Json,
    });

    if (error) {
      console.error("recordMarketingContact failed", error);
      return { success: false, error: "db_error" };
    }
    return { success: true, marketingConsent: hasMarketingConsent };
  });

const liveSessionsSchema = z.object({
  windowMinutes: z.number().min(1).max(60).default(5),
});

/** Admin-only: fetch sessions seen in the last N minutes for the live map. */
export const getLiveVisitorSessions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => liveSessionsSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdminOrModerator(context.userId);
    const cutoff = new Date(Date.now() - data.windowMinutes * 60_000).toISOString();
    const { data: rows, error } = await supabaseAdmin
      .from("visitor_sessions")
      .select(
        "session_id, visitor_id, city, region, country, latitude, longitude, current_path, last_seen_at, started_at",
      )
      .gte("last_seen_at", cutoff)
      .not("latitude", "is", null)
      .not("longitude", "is", null)
      .order("last_seen_at", { ascending: false })
      .limit(500);

    if (error) {
      console.error("getLiveVisitorSessions failed", error);
      return { sessions: [], error: error.message };
    }
    return { sessions: rows ?? [], error: null };
  });

const topCitiesSchema = z.object({
  days: z.number().min(1).max(365).default(30),
  limit: z.number().min(1).max(50).default(10),
});

export const getTopVisitorCities = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => topCitiesSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdminOrModerator(context.userId);
    const cutoff = new Date(Date.now() - data.days * 24 * 60 * 60_000).toISOString();
    const { data: rows, error } = await supabaseAdmin
      .from("visitor_sessions")
      .select("city, country, visitor_id")
      .gte("started_at", cutoff)
      .not("city", "is", null);

    if (error) {
      console.error("getTopVisitorCities failed", error);
      return { cities: [], error: error.message };
    }

    const map = new Map<string, { city: string; country: string | null; visitors: Set<string> }>();
    for (const r of rows ?? []) {
      const key = `${r.city}|${r.country ?? ""}`;
      const entry = map.get(key) ?? { city: r.city as string, country: r.country, visitors: new Set() };
      entry.visitors.add(r.visitor_id as string);
      map.set(key, entry);
    }

    const cities = Array.from(map.values())
      .map((c) => ({ city: c.city, country: c.country, visitors: c.visitors.size }))
      .sort((a, b) => b.visitors - a.visitors)
      .slice(0, data.limit);

    return { cities, error: null };
  });

const cityHeatmapSchema = z.object({
  days: z.number().min(1).max(365).default(30),
  limit: z.number().min(1).max(200).default(50),
});

/** Admin-only: aggregated city visitor counts with coordinates for heat-map overlay. */
export const getCityHeatmap = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => cityHeatmapSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdminOrModerator(context.userId);
    const cutoff = new Date(Date.now() - data.days * 24 * 60 * 60_000).toISOString();
    const { data: rows, error } = await supabaseAdmin
      .from("visitor_sessions")
      .select("city, country, latitude, longitude, visitor_id")
      .gte("started_at", cutoff)
      .not("city", "is", null)
      .not("latitude", "is", null)
      .not("longitude", "is", null);

    if (error) {
      console.error("getCityHeatmap failed", error);
      return { points: [], error: error.message };
    }

    type Bucket = {
      city: string;
      country: string | null;
      latSum: number;
      lngSum: number;
      samples: number;
      visitors: Set<string>;
    };
    const map = new Map<string, Bucket>();
    for (const r of rows ?? []) {
      const lat = Number(r.latitude);
      const lng = Number(r.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const key = `${r.city}|${r.country ?? ""}`;
      const entry = map.get(key) ?? {
        city: r.city as string,
        country: r.country,
        latSum: 0,
        lngSum: 0,
        samples: 0,
        visitors: new Set<string>(),
      };
      entry.latSum += lat;
      entry.lngSum += lng;
      entry.samples += 1;
      entry.visitors.add(r.visitor_id as string);
      map.set(key, entry);
    }

    const points = Array.from(map.values())
      .map((c) => ({
        city: c.city,
        country: c.country,
        latitude: c.latSum / c.samples,
        longitude: c.lngSum / c.samples,
        visitors: c.visitors.size,
      }))
      .sort((a, b) => b.visitors - a.visitors)
      .slice(0, data.limit);

    return { points, error: null };
  });

const marketingContactsSchema = z.object({
  limit: z.number().min(1).max(200).default(50),
  marketingConsentOnly: z.boolean().default(false),
});

export const listMarketingContacts = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => marketingContactsSchema.parse(input))
  .handler(async ({ data, context }) => {
    await requireAdminOrModerator(context.userId);
    let query = supabaseAdmin
      .from("marketing_contacts")
      .select("id, source, email_plain, phone_plain, full_name_plain, city, consent_marketing, created_at")
      .order("created_at", { ascending: false })
      .limit(data.limit);

    if (data.marketingConsentOnly) {
      query = query.eq("consent_marketing", true);
    }

    const { data: rows, error } = await query;
    if (error) {
      console.error("listMarketingContacts failed", error);
      return { contacts: [], error: error.message };
    }
    return { contacts: rows ?? [], error: null };
  });
